import {
  ClientApplicationFragment,
  useClientApplicationsQuery,
} from "@graphql/client-applications/ClientApplications.g";
import {
  type ColumnDef,
  PaginationState,
  SortingState,
  createColumnHelper,
} from "@tanstack/react-table";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router";

import { useClientTotalUsersStatQuery } from "@components/Stats/ClientTotalUsersStat/ClientTotalUsersStat.g";

import { Box } from "@design-system/Box/Box";
import { Clickable } from "@design-system/Clickable/Clickable";
import { Table } from "@design-system/Table/Table";
import { Tag } from "@design-system/Tag/Tag";

import { routes } from "@pages/routes";

const PAGE_SIZE = 5;

const columnHelper = createColumnHelper<ClientApplicationFragment>();

type Props = {
  clientId: string;
  initialPageIndex?: number;
};

export const ApplicationsMostUsedByClientTable = ({
  clientId,
  initialPageIndex = 0,
}: Props) => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: initialPageIndex,
    pageSize: PAGE_SIZE,
  });
  // Fetch more results and sort on frontend since employee count sorting not supported on backend
  const clientAppsQuery = useClientApplicationsQuery({
    clientId,
    first: 5, // Fetch more to sort properly
    offset: 0,
    resource_IsPrimaryResource: true,
    orderBy: "-rg_total_employees",
  });
  const navigate = useNavigate();
  const isClientsLoading = clientAppsQuery.isLoading;
  const isClientsFetching = clientAppsQuery.isFetching;

  const userStats = useClientTotalUsersStatQuery({
    clientId,
  });

  // Sort by employee count (most used) and paginate on frontend
  const allData = useMemo(() => {
    const apps =
      clientAppsQuery.data?.clientApplications?.edges
        ?.map((edge) => edge?.node)
        ?.filter((clientApplication) => !!clientApplication) ?? [];

    // Sort by employee count descending
    return apps.sort((a, b) => {
      const aCount = a?.employees?.totalCount ?? 0;
      const bCount = b?.employees?.totalCount ?? 0;
      return bCount - aCount;
    });
  }, [clientAppsQuery.data]);

  const data = useMemo(() => {
    const start = pagination.pageIndex * pagination.pageSize;
    const end = start + pagination.pageSize;
    return allData.slice(start, end);
  }, [allData, pagination]);

  const handleApplicationDashboardClick = useCallback(
    (applicationId: string) => {
      navigate(
        routes.portal.clients.client.applications.application.dashboard.$buildPath(
          {
            params: { clientId, applicationId },
          },
        ),
        {
          viewTransition: true,
        },
      );
    },
    [clientId, navigate],
  );

  const calculatePercentage = useCallback(
    (value: number | undefined) => {
      const totalUsers = userStats.data?.client?.employees?.totalCount ?? 0;
      if (
        value === undefined ||
        userStats.data?.client?.employees?.totalCount === 0
      ) {
        return "0%";
      }
      const percentage = (value / totalUsers) * 100;
      return `${percentage.toFixed(2)}%`;
    },
    [userStats.data],
  );

  const columns = useMemo(
    () =>
      [
        columnHelper.accessor("resource.productName", {
          id: "resource__product_name",
          cell: (props) => {
            return (
              <Clickable
                onClick={() =>
                  handleApplicationDashboardClick(props.row.original.id)
                }
                outline="sm"
                stretch
              >
                <Table.CompanyCell
                  name={props.getValue() ?? ""}
                  subtext={props.row.original.resource?.vendorName || undefined}
                  signedLogoUrl={
                    props.row.original.resource?.publicResource
                      ?.signedLogoUrl || undefined
                  }
                  domain={props.row.original.resource?.productDomainName ?? ""}
                />
              </Clickable>
            );
          },
          footer: undefined,
        }),
        columnHelper.accessor("employees.totalCount", {
          id: "employees__total_count",
          cell: (props) => {
            return (
              <Box justify="flex-end" stretch>
                <Tag
                  text={`${calculatePercentage(props.getValue() || 0)} of total users`}
                  title={`Users: ${props.getValue() ?? 0}`}
                />
              </Box>
            );
          },
          footer: undefined,
        }),
      ] as ColumnDef<ClientApplicationFragment>[],
    [handleApplicationDashboardClick, calculatePercentage],
  );

  const handlePageSizeChange = useCallback((pageSize: number) => {
    setPagination({ pageIndex: 0, pageSize });
  }, []);

  return (
    <>
      <Table
        columns={columns}
        data={data}
        rowCount={allData.length}
        onPaginationChange={setPagination}
        pagination={pagination}
        isLoading={isClientsLoading}
        isFetching={isClientsFetching}
        onPageSizeChange={handlePageSizeChange}
        sorting={sorting}
        onSortingChange={setSorting}
        showPageSizeDropdown={false}
        showHeader={false}
        showFooter={false}
      />
    </>
  );
};
